// TikTok Pixel event helpers.
// All calls are safe no-ops when the SDK isn't loaded (e.g. on preview, or
// when the user declined analytics cookies — `holdConsent` swallows events).

const safeCall = (fn) => {
  try {
    if (typeof window !== 'undefined' && window.ttq && typeof window.ttq[fn] === 'function') {
      return window.ttq[fn];
    }
  } catch (e) { /* ignored */ }
  return null;
};

// Identify the user once they log in / sign up. TikTok strongly recommends
// hashing PII server-side, but the browser SDK auto-hashes anything you pass.
export const tiktokIdentify = ({ email, externalId, phone } = {}) => {
  const identify = safeCall('identify');
  if (!identify) return;
  const payload = {};
  if (email) payload.email = String(email).toLowerCase().trim();
  if (externalId) payload.external_id = String(externalId);
  if (phone) payload.phone_number = String(phone);
  if (Object.keys(payload).length === 0) return;
  identify(payload);
};

// Generic track wrapper — TikTok's standard events are listed at
// https://business-api.tiktok.com/portal/docs?id=1739585702064129
export const tiktokTrack = (eventName, params = {}) => {
  const track = safeCall('track');
  if (!track) return;
  track(eventName, params);
};

// --- Standard conversion events for the JobSurf funnel ---

export const trackSignup = ({ method = 'email', email, userId } = {}) => {
  tiktokIdentify({ email, externalId: userId });
  // CompleteRegistration is the canonical TikTok event for "user signed up".
  tiktokTrack('CompleteRegistration', { content_name: `signup_${method}` });
};

export const trackStartTrial = ({ value = 0, currency = 'EUR', email, userId } = {}) => {
  tiktokIdentify({ email, externalId: userId });
  tiktokTrack('StartTrial', { value, currency });
};

// Fires when user lands on Stripe Checkout (add-to-cart equivalent).
export const trackInitiateCheckout = ({ value = 0, currency = 'EUR', plan = '' } = {}) => {
  tiktokTrack('InitiateCheckout', { value, currency, content_name: plan });
};

// Fires after a successful subscription payment.
export const trackSubscribe = ({ value = 0, currency = 'EUR', plan = '', email, userId } = {}) => {
  tiktokIdentify({ email, externalId: userId });
  tiktokTrack('Subscribe', { value, currency, content_name: plan });
  // Also fire `Purchase` so it matches TikTok's revenue dashboard out of the box.
  tiktokTrack('Purchase', { value, currency, content_name: plan });
};
